import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

/** Formatos de código que se intentan leer con la cámara. */
export const FORMATOS_ESCANEO = [
  'ean_13',
  'ean_8',
  'upc_a',
  'upc_e',
  'code_128',
  'code_39',
  'itf',
  'qr_code',
] as const;

export interface ResultadoEscaneo {
  codigo: string;
  formato: string;
}

export type DetenerEscaneo = () => void;

interface ResultadoDetectorNativo {
  rawValue: string;
  format: string;
}

interface DetectorNativo {
  detect(origen: CanvasImageSource): Promise<ResultadoDetectorNativo[]>;
}

interface ConstructorDetectorNativo {
  new (opciones: { formats: string[] }): DetectorNativo;
  getSupportedFormats?: () => Promise<string[]>;
}

/**
 * Motor de lectura de códigos de barras por cámara.
 *
 * Usa la API nativa `BarcodeDetector` cuando está disponible (Android/Chrome) y
 * cae a ZXing en los navegadores que no la soportan (iOS/Safari). ZXing se importa
 * de forma dinámica para no cargarlo en los dispositivos que no lo necesitan.
 */
@Injectable({ providedIn: 'root' })
export class EscanerCodigoService {
  private readonly platformId = inject(PLATFORM_ID);

  /** Indica si el navegador actual puede abrir la cámara para escanear. */
  get soportado(): boolean {
    return (
      isPlatformBrowser(this.platformId) &&
      typeof navigator !== 'undefined' &&
      Boolean(navigator.mediaDevices?.getUserMedia)
    );
  }

  /** Solicita la cámara trasera del dispositivo. */
  async obtenerStream(): Promise<MediaStream> {
    if (!this.soportado) {
      throw new Error('El escaneo con cámara no está disponible en este dispositivo.');
    }

    return navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
      audio: false,
    });
  }

  /** Inicia el loop de detección y devuelve la función para detenerlo. */
  async iniciarDeteccion(
    video: HTMLVideoElement,
    onDetectado: (resultado: ResultadoEscaneo) => void,
  ): Promise<DetenerEscaneo> {
    const detectorNativo = await this.crearDetectorNativo();

    if (detectorNativo) {
      return this.detectarConApiNativa(video, detectorNativo, onDetectado);
    }

    return this.detectarConZxing(video, onDetectado);
  }

  /** Indica si la cámara activa tiene linterna disponible. */
  tieneLinterna(stream: MediaStream): boolean {
    const track = stream.getVideoTracks()[0];
    if (!track || typeof track.getCapabilities !== 'function') return false;
    const capacidades = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };
    return capacidades.torch === true;
  }

  /** Enciende o apaga la linterna. Devuelve el estado final. */
  async alternarLinterna(stream: MediaStream): Promise<boolean> {
    const track = stream.getVideoTracks()[0];
    if (!track) return false;

    const settings = track.getSettings() as MediaTrackSettings & { torch?: boolean };
    const encendida = settings.torch === true;

    await track.applyConstraints({
      advanced: [{ torch: !encendida }],
    } as unknown as MediaTrackConstraints);

    return !encendida;
  }

  private async crearDetectorNativo(): Promise<DetectorNativo | null> {
    if (!isPlatformBrowser(this.platformId)) return null;

    const global = globalThis as typeof globalThis & {
      BarcodeDetector?: ConstructorDetectorNativo;
    };
    const Constructor = global.BarcodeDetector;
    if (!Constructor) return null;

    try {
      const disponibles = Constructor.getSupportedFormats
        ? await Constructor.getSupportedFormats()
        : [...FORMATOS_ESCANEO];
      const formatos = FORMATOS_ESCANEO.filter((formato) => disponibles.includes(formato));

      // Si no soporta EAN-13, no vale la pena usar la API nativa para productos.
      if (!formatos.includes('ean_13')) return null;

      return new Constructor({ formats: [...formatos] });
    } catch {
      return null;
    }
  }

  private detectarConApiNativa(
    video: HTMLVideoElement,
    detector: DetectorNativo,
    onDetectado: (resultado: ResultadoEscaneo) => void,
  ): DetenerEscaneo {
    const INTERVALO_MS = 200;
    let activo = true;
    let frame: number | null = null;
    let detectando = false;
    let ultimoIntento = 0;

    const ciclo = async (marca: number) => {
      if (!activo) return;

      if (
        !detectando &&
        video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
        marca - ultimoIntento >= INTERVALO_MS
      ) {
        ultimoIntento = marca;
        detectando = true;

        try {
          const resultados = await detector.detect(video);
          const encontrado = resultados.find((resultado) => resultado.rawValue?.trim());
          if (encontrado && activo) {
            onDetectado({
              codigo: encontrado.rawValue.trim(),
              formato: encontrado.format,
            });
          }
        } catch {
          // El frame todavía no está listo; se reintenta en el siguiente ciclo.
        } finally {
          detectando = false;
        }
      }

      if (activo) {
        frame = requestAnimationFrame((siguiente) => void ciclo(siguiente));
      }
    };

    frame = requestAnimationFrame((marca) => void ciclo(marca));

    return () => {
      activo = false;
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }

  private async detectarConZxing(
    video: HTMLVideoElement,
    onDetectado: (resultado: ResultadoEscaneo) => void,
  ): Promise<DetenerEscaneo> {
    const [{ BrowserMultiFormatReader, BarcodeFormat }, { DecodeHintType }] = await Promise.all([
      import('@zxing/browser'),
      import('@zxing/library'),
    ]);

    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.CODE_128,
      BarcodeFormat.CODE_39,
      BarcodeFormat.ITF,
      BarcodeFormat.QR_CODE,
    ]);

    const lector = new BrowserMultiFormatReader(hints, {
      delayBetweenScanAttempts: 200,
      delayBetweenScanSuccess: 500,
    });

    const controles = await lector.decodeFromVideoElement(video, (resultado) => {
      const codigo = resultado?.getText()?.trim();
      if (!codigo) return;

      const formato = resultado?.getBarcodeFormat();
      onDetectado({
        codigo,
        formato: formato != null ? (BarcodeFormat[formato] ?? String(formato)) : 'desconocido',
      });
    });

    return () => controles.stop();
  }
}
